import React, { useEffect, useMemo, useState } from 'react';
import api from './api';
import { Mail, Trash2, Eye, EyeOff, Loader2, Inbox } from 'lucide-react';

export default function InboxPanel() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/api/admin/inquiries');
      setItems(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (filter === 'unread') return items.filter((i) => !i.read);
    if (filter === 'read') return items.filter((i) => i.read);
    return items;
  }, [items, filter]);

  const toggleRead = async (it) => {
    await api.patch(`/api/admin/inquiries/${it.id}/read`, null, { params: { read: !it.read } });
    setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, read: !it.read } : x)));
  };

  const remove = async (it) => {
    if (!window.confirm('Delete this submission?')) return;
    await api.delete(`/api/admin/inquiries/${it.id}`);
    setItems((arr) => arr.filter((x) => x.id !== it.id));
    if (openId === it.id) setOpenId(null);
  };

  if (items === null) {
    return <div className="flex items-center gap-2 text-white/60 py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="inbox-panel">
      <div className="lg:col-span-5 bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl overflow-hidden">
        <div className="flex items-center gap-1 p-3 border-b border-[#D4AF37]/10 bg-[#083325]">
          {['all', 'unread', 'read'].map((k) => (
            <button
              key={k}
              data-testid={`inbox-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] transition-colors ${
                filter === k ? 'bg-[#D4AF37] text-[#0B3D2E]' : 'text-white/70 hover:text-[#D4AF37]'
              }`}
            >
              {k}
            </button>
          ))}
          <span className="ml-auto text-xs text-white/55">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        <ul className="max-h-[640px] overflow-auto divide-y divide-[#D4AF37]/10">
          {filtered.length === 0 && (
            <li className="p-10 text-center text-white/55">
              <Inbox className="w-8 h-8 mx-auto mb-3 opacity-50" />
              No submissions yet.
            </li>
          )}
          {filtered.map((it) => (
            <li
              key={it.id}
              onClick={() => setOpenId(it.id)}
              data-testid={`inbox-item-${it.id}`}
              className={`p-4 cursor-pointer transition-colors hover:bg-[#083325] ${openId === it.id ? 'bg-[#083325]' : ''}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  {!it.read && <span className="w-2 h-2 rounded-full bg-[#D4AF37] shrink-0" />}
                  <span className={`truncate text-sm ${it.read ? 'text-white/65' : 'text-white font-semibold'}`}>{it.name}</span>
                </div>
                <span className="text-[11px] text-white/45 shrink-0">{new Date(it.created_at).toLocaleDateString()}</span>
              </div>
              <div className="text-xs text-[#D4AF37] truncate">{it.service || '—'}</div>
              <div className="text-xs text-white/55 truncate mt-1">{it.email}</div>
            </li>
          ))}
        </ul>
      </div>

      <div className="lg:col-span-7 bg-[#124D3A] border border-[#D4AF37]/15 rounded-3xl p-7 min-h-[400px]" data-testid="inbox-detail">
        {(() => {
          const it = items.find((x) => x.id === openId);
          if (!it) return <div className="h-full flex items-center justify-center text-white/55"><Mail className="w-5 h-5 mr-2" /> Select a submission to view details.</div>;
          return (
            <div>
              <div className="flex items-start justify-between gap-3 mb-6">
                <div>
                  <h3 className="font-serif-display text-2xl text-white">{it.name}</h3>
                  <a href={`mailto:${it.email}`} className="text-sm text-[#D4AF37] hover:underline">{it.email}</a>
                  <div className="text-xs text-white/55 mt-1">{new Date(it.created_at).toLocaleString()} · {it.locale?.toUpperCase()}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleRead(it)} data-testid="inbox-toggle-read" className="px-3 py-2 rounded-full border border-[#D4AF37]/30 text-xs text-white/85 hover:text-[#D4AF37] flex items-center gap-1.5">
                    {it.read ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    Mark {it.read ? 'unread' : 'read'}
                  </button>
                  <button onClick={() => remove(it)} data-testid="inbox-delete" className="px-3 py-2 rounded-full bg-red-500/15 text-red-200 border border-red-400/30 text-xs flex items-center gap-1.5 hover:bg-red-500/25">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                <Detail label="Phone">{it.country ? `${it.country} ` : ''}{it.phone}</Detail>
                <Detail label="Service of interest">{it.service || '—'}</Detail>
              </div>
              <Detail label="Message">
                <p className="whitespace-pre-wrap text-white/85 leading-relaxed">{it.message || '—'}</p>
              </Detail>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.18em] uppercase text-[#D4AF37] mb-1.5">{label}</div>
      <div className="text-white/90">{children}</div>
    </div>
  );
}
